import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, AlertCircle, X } from 'lucide-react';
import { useBankTransferQueue } from '../hooks/useBankTransfers';
import { BankTransferStatusBadge } from '../components/BankTransferStatusBadge';
import { BankTransferDetailPanel } from '../components/BankTransferDetailPanel';
import type { BankTransferProcessStatus, BankTransferQueueItem } from '../types/bankTransfer';
import { ACTIVE_BANK_TRANSFER_STATUSES, TERMINAL_BANK_TRANSFER_STATUSES } from '../types/bankTransfer';
import { formatDate } from '../utils/formatDate';


// ── Urgency sort ───────────────────────────────────────────────────────────────

const URGENCY_ORDER: Record<BankTransferProcessStatus, number> = {
  AWAITING_TRANSFER: 3,
  DECLARED_AWAITING_SETTLEMENT: 2,
  DEPOSIT_CONFIRMED: 3,
  AWAITING_BALANCE: 3,
  COMPLETED: 5,
  EXPIRED: 4,
  LATE_FUNDS_REVIEW: 4,
  REFUND_REQUIRED: 4,
  CANCELLED: 5,
};

const isOverdue = (item: BankTransferQueueItem): boolean => {
  if (!item.transferDueAt) return false;
  if (!ACTIVE_BANK_TRANSFER_STATUSES.includes(item.processStatus)) return false;
  return new Date(item.transferDueAt) < new Date();
};

const urgencySort = (a: BankTransferQueueItem, b: BankTransferQueueItem): number => {
  const aOverdue = isOverdue(a) ? 0 : 1;
  const bOverdue = isOverdue(b) ? 0 : 1;
  if (aOverdue !== bOverdue) return aOverdue - bOverdue;

  const aOrder = isOverdue(a) ? 0 : URGENCY_ORDER[a.processStatus] ?? 5;
  const bOrder = isOverdue(b) ? 0 : URGENCY_ORDER[b.processStatus] ?? 5;
  return aOrder - bOrder;
};

const BankTransfers = () => {
  const { t } = useTranslation(['pages', 'badges']);

  const STATUS_LABELS: Record<BankTransferProcessStatus, string> = {
    AWAITING_TRANSFER: t('badges:bankTransferAwaitingTransfer'),
    DECLARED_AWAITING_SETTLEMENT: t('badges:bankTransferDeclaredAwaitingSettlement'),
    DEPOSIT_CONFIRMED: t('badges:bankTransferDepositConfirmed'),
    AWAITING_BALANCE: t('badges:bankTransferAwaitingBalance'),
    COMPLETED: t('badges:bankTransferCompleted'),
    EXPIRED: t('badges:bankTransferExpired'),
    LATE_FUNDS_REVIEW: t('badges:bankTransferLateFundsReview'),
    REFUND_REQUIRED: t('badges:bankTransferRefundRequired'),
    CANCELLED: t('badges:bankTransferCancelled'),
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const statusParam = searchParams.get('status') as BankTransferProcessStatus | null;
  const searchParam = searchParams.get('search') ?? '';

  const queryStatus = statusParam ?? undefined;

  const { data, isLoading, isError, refetch } = useBankTransferQueue(
    queryStatus ? { status: queryStatus } : undefined,
  );

  const items = data?.items ?? [];
  const sorted = [...items].sort(urgencySort);

  const filtered = searchParam
    ? sorted.filter(
        item =>
          item.guestEmail.toLowerCase().includes(searchParam.toLowerCase()) ||
          (item.paymentReference ?? '').toLowerCase().includes(searchParam.toLowerCase()),
      )
    : sorted;

  const handleStatusChange = useCallback(
    (value: string) => {
      const next = new URLSearchParams(searchParams);
      if (value) {
        next.set('status', value);
      } else {
        next.delete('status');
      }
      next.delete('page');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      const next = new URLSearchParams(searchParams);
      if (value) {
        next.set('search', value);
      } else {
        next.delete('search');
      }
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const handleClearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const selectedItem = filtered.find(item => item.bookingId === selectedBookingId) ?? null;

  const hasFilters = !!statusParam || !!searchParam;

  return (
    <div className="relative">
      {/* Success banner */}
      {successMessage && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
          <CheckCircle size={16} className="shrink-0" />
          <span className="flex-1">{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)} aria-label="Dismiss">
            <X size={16} />
          </button>
        </div>
      )}

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('pages:bankTransfersHeading')}</h1>
        <p className="text-gray-600 mt-2">{t('pages:bankTransfersSubHeading')}</p>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-3 mb-6">
        <select
          value={statusParam ?? ''}
          onChange={e => handleStatusChange(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          aria-label={t('pages:bankTransfersFilterStatus')}
        >
          <option value="">{t('pages:bankTransfersFilterStatusAll')}</option>
          {ACTIVE_BANK_TRANSFER_STATUSES.map(s => (
            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
          ))}
          <option value="_terminal" disabled>──</option>
          {TERMINAL_BANK_TRANSFER_STATUSES.map(s => (
            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
          ))}
        </select>

        <input
          type="text"
          value={searchParam}
          onChange={e => handleSearchChange(e.target.value)}
          placeholder={t('pages:bankTransfersFilterSearch')}
          className="flex-1 min-w-48 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
        />

        {hasFilters && (
          <button
            onClick={handleClearFilters}
            className="text-sm text-gray-500 hover:text-gray-700 px-3 py-2 border border-gray-200 rounded-lg transition-colors"
          >
            {t('pages:bankTransfersClearFilters')}
          </button>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <p className="text-gray-500 text-sm">{t('pages:bankTransfersLoading')}</p>
      ) : isError ? (
        <div className="flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle size={16} />
          <span>Failed to load bank transfers.</span>
          <button onClick={() => refetch()} className="underline hover:no-underline">Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-gray-500 text-sm">
          {hasFilters ? t('pages:bankTransfersEmpty') : t('pages:bankTransfersEmptyAll')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                <th className="pb-3 pr-4">{t('pages:bankTransfersColumnApartment')}</th>
                <th className="pb-3 pr-4">{t('pages:bankTransfersColumnGuest')}</th>
                <th className="pb-3 pr-4">{t('pages:bankTransfersColumnCheckIn')}</th>
                <th className="pb-3 pr-4">{t('pages:bankTransfersColumnCheckOut')}</th>
                <th className="pb-3 pr-4">{t('pages:bankTransfersColumnExpectedStage')}</th>
                <th className="pb-3 pr-4">{t('pages:bankTransfersColumnExpectedAmount')}</th>
                <th className="pb-3 pr-4">{t('pages:bankTransfersColumnReference')}</th>
                <th className="pb-3 pr-4">{t('pages:bankTransfersColumnDeadline')}</th>
                <th className="pb-3">{t('pages:bankTransfersColumnStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(item => {
                const overdue = isOverdue(item);
                return (
                  <tr
                    key={item.bookingId}
                    onClick={() =>
                      setSelectedBookingId(prev =>
                        prev === item.bookingId ? null : item.bookingId,
                      )
                    }
                    className={`border-b border-gray-100 cursor-pointer transition-colors hover:bg-gray-50 ${
                      selectedBookingId === item.bookingId ? 'bg-primary-50' : ''
                    }`}
                  >
                    <td className="py-3 pr-4 font-medium text-gray-900">
                      {item.apartmentName ?? '—'}
                    </td>
                    <td className="py-3 pr-4">
                      <div className="text-gray-900">{item.guestName ?? item.guestEmail}</div>
                      {item.guestName && (
                        <div className="text-xs text-gray-400">{item.guestEmail}</div>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-gray-700">{formatDate(item.checkIn)}</td>
                    <td className="py-3 pr-4 text-gray-700">{formatDate(item.checkOut)}</td>
                    <td className="py-3 pr-4 text-gray-700">{item.expectedStage}</td>
                    <td className="py-3 pr-4 text-gray-700">
                      {item.expectedAmount.toFixed(2)} {item.currency}
                    </td>
                    <td className="py-3 pr-4 font-mono text-xs text-gray-600">
                      {item.paymentReference ?? '—'}
                    </td>
                    <td className="py-3 pr-4">
                      {item.transferDueAt ? (
                        <span className={overdue ? 'text-red-600 font-medium' : 'text-gray-700'}>
                          {overdue && (
                            <span className="mr-1 text-xs font-semibold uppercase text-red-500">
                              {t('pages:bankTransfersUrgencyOverdue')}
                            </span>
                          )}
                          {formatDate(item.transferDueAt.slice(0, 10))}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-3">
                      <div className="flex flex-col gap-1">
                        <BankTransferStatusBadge status={item.processStatus} />
                        {item.declaration && (
                          <span className="inline-flex items-center gap-1 text-xs text-blue-600 font-medium">
                            <span>📋</span>
                            {t('pages:bankTransfersDeclaredIndicator')}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail panel */}
      {selectedItem && (
        <BankTransferDetailPanel
          item={selectedItem}
          onClose={() => setSelectedBookingId(null)}
          onActionSuccess={(message: string) => {
            setSuccessMessage(message);
            setSelectedBookingId(null);
          }}
        />
      )}
    </div>
  );
};

export default BankTransfers;
